/**
 * API-specific type definitions for backend integration
 * These types match the actual API response formats
 */

import type { ApiBusinessImpactValues } from '../services/businessImpactService'

// API_BASE_URL is the prefix used to call the backend.
// - Use VITE_API_URL for remote backend (e.g. https://api.example.com)
// - Fallback to /api for local dev (Vite proxy forwards to BACKEND_URL)
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Default fetch options for API calls
 * - credentials: 'include' - Required to send/receive HttpOnly cookies
 */
export const API_FETCH_OPTIONS: RequestInit = {
  credentials: 'include',
}

// Security issues count type matching backend SecurityIssueToCount
export type SecurityIssueToCount = {
  vulnerabilities: number
  events: number
  incidents: number
  sensitive_data: number
  misconfigurations: number
}

export type BackendRiskScoreComponents = {
  // Base probability components (0.0-1.0)
  p_exploited: number
  p_severity: number
  p_reachable: number
  p_external_threat_activity: number
  e_controls: number
  impact: number
  // Intermediate calculations
  p_exploitable: number // P_exploited × P_severity
  p_inherent_risk: number // P_reachable × P_exploitable (baseline)
  likelihood: number // (P_inherent_risk + Threat_Boost) × (1 - E_controls)
}

export type BackendRiskScoreDetails = {
  // Reachability details
  is_internet_exposed: boolean | null
  in_blast_radius: boolean | null
  blast_radius_hop_distance: number | null
  // Business impact
  bi_value_computed: number
  // GuardDuty threat intelligence
  guardduty_severity: string | null
  guardduty_last_seen: string | null
  // Security issue metadata (may be NULL for clean resources)
  has_cisa_kev: boolean
  epss_score: number | null
  exploit_available: boolean
  cvss_score: number | null
  severity: string
  cve_id: string
  package_name: string
  fixed_version: string
  active_incident_count: number
  impact_explanation?: string
  // Sensitive-data-specific finding details
  data_type?: string
  exposure_level?: string
  occurrence_count?: number
  // Misconfiguration-specific finding details
  category?: string
  rule?: string
}

// BackendScoreFactor is the wire shape of one D0 risk_score_factors array element.
// snake_case fields match the SQL json_build_object output; optional fields are absent
// when null (omitempty on the Go side). Stage B Phase 5 imports this type — do not duplicate.
export type BackendScoreFactor = {
  key: string
  label: string
  category: string
  state: string // active | suppressed | pending (open; coerce unknowns)
  direction: string // elevating | reducing | context (open; coerce unknowns)
  explanation: string
  contribution?: number | null // null for context-direction factors
  weight_pct?: number | null
  at_max?: boolean // numeric factor that cannot raise the already-capped (100) score
  resource_id?: string | null // drill-through target resource; category names its inventory layer
  severity?: string | null
  tooltip?: string | null
  days_active?: number | null
  hop_distance?: number | null // blast_radius factor only: hops to the internet-reachable resource
  icon?: string | null
  guidance?: {
    description: string
    time_estimate: string
    risk_reduction: number
  } | null
}

export type BackendRiskScoreData = {
  score: number
  components: BackendRiskScoreComponents
  details: BackendRiskScoreDetails
  risk_score_factors?: BackendScoreFactor[]
}

export const EMPTY_BACKEND_RISK_SCORE_DATA: BackendRiskScoreData = {
  score: 0,
  components: {
    p_exploited: 0,
    p_severity: 0,
    p_reachable: 0,
    p_external_threat_activity: 0,
    e_controls: 0,
    impact: 0,
    p_exploitable: 0,
    p_inherent_risk: 0,
    likelihood: 0,
  },
  details: {
    is_internet_exposed: false,
    in_blast_radius: false,
    blast_radius_hop_distance: null,
    bi_value_computed: 0,
    guardduty_severity: null,
    guardduty_last_seen: null,
    has_cisa_kev: false,
    epss_score: null,
    exploit_available: false,
    cvss_score: null,
    severity: 'none',
    cve_id: '',
    package_name: '',
    fixed_version: '',
    active_incident_count: 0,
  },
}

// ComputeBaseResource is a base type that has fields common to all compute
// resource types. Concrete compute resource types specialize this base for type
// narrowing. ComputeBaseResource is forward compatible with the API, such that
// adding new resource types to the API will result ComputeBaseResource being
// unable to be narrowed, which we can catch and handle via the type system, as
// opposed to runtime.
type ComputeBaseApiResource = {
  // Multi-cloud identifiers
  id?: string // Primary ID field for drawer URLs (when available)
  cloud_provider: string
  resource_id: string
  resource_type: string

  // Cloud context
  account_id: string
  region_name: string

  // Soft delete fields
  is_deleted: boolean
  deleted_at?: string

  // Audit fields
  created_at: string
  updated_at: string
  first_seen_at?: string
  last_seen_at?: string

  // Provider-specific resource data
  resource_data?: unknown

  // Custom tags created by users
  custom_tags?: string[]

  // Environment field from backend
  environment?: string

  // Detection method field from backend
  source_system?: string

  // Azure VM metadata fields (from compute_inventory)
  kernel_version?: string | null
  os_type?: string | null
  os_sku?: string | null
  vm_size?: string | null
  resource_group?: string | null
  subscription_id?: string | null

  // Deep link to the resource in the cloud provider console
  console_url?: string

  // Security issues associated with this resource
  security_issues: SecurityIssueToCount

  // Risk indicators associated with this resource
  risk_indicators?: {
    reachable: {
      is_active?: boolean
      score?: number
      description: string
      indicators?: {
        display: string
        type: string
        count: number
      }[]
    }
    exploitable: {
      is_active?: boolean
      score?: number
      description: string
      indicators?: {
        display: string
        type: string
        count: number
      }[]
    }
    active_incident: {
      is_active?: boolean
      count?: number
      description: string
      indicators?: {
        display: string
        type: string
        count: number
      }[]
    }
  }
} & ApiBusinessImpactValues

export type AzureVMDiscoverySummary = {
  vm_count: number
  vmss_count: number
  linux_vm_count: number
  linux_vmss_count: number
}

export type ComputeAwsLambdaApiResource = ComputeBaseApiResource & {
  cloud_provider: 'aws'
  resource_type: 'aws.lambda'
  resource_data: AwsLambdaApiResourceData
  risk_score_data: BackendRiskScoreData
}

export type ComputeAzureFunctionApiResource = ComputeBaseApiResource & {
  cloud_provider: 'azure'
  resource_type: 'azure.functions'
  resource_data: AzureFunctionApiResourceData
  risk_score_data?: BackendRiskScoreData
}

export type ComputeAzureVMApiResource = ComputeBaseApiResource & {
  cloud_provider: 'azure'
  resource_type: 'azure.compute.virtualmachine'
  resource_data: AzureVMApiResourceData
  risk_score_data?: BackendRiskScoreData
}

export type ComputeAzureVMSSApiResource = ComputeBaseApiResource & {
  cloud_provider: 'azure'
  resource_type: 'azure.compute.virtualmachinescaleset'
  resource_data: AzureVMSSApiResourceData
  risk_score_data?: BackendRiskScoreData
}

export type ComputeAwsEc2ApiResource = ComputeBaseApiResource & {
  cloud_provider: 'aws'
  resource_type: 'aws.ec2'
  resource_data: AwsEc2ApiResourceData
  risk_score_data: BackendRiskScoreData
}

export type ComputeAwsEcsApiResource = ComputeBaseApiResource & {
  cloud_provider: 'aws'
  resource_type: string // Generic ECS resource type - can be aws.ecs.service, aws.ecs.cluster, aws.ecs.task, etc.
  resource_data: AwsEcsGenericApiResourceData
  risk_score_data: BackendRiskScoreData
}

// GCP compute rows. resource_data is the Cloud Asset Inventory `resource.data`
// payload, i.e. the native service API shape (Compute API Instance, Cloud
// Functions v2 Function, Cloud Run v1 Knative Service) -- see the *ApiResourceData
// types below for the fields the UI reads.
export type ComputeGcpInstanceApiResource = ComputeBaseApiResource & {
  cloud_provider: 'gcp'
  resource_type: 'gcp.compute.instance'
  resource_data: GcpComputeInstanceApiResourceData
  risk_score_data?: BackendRiskScoreData
}

export type ComputeGcpFunctionApiResource = ComputeBaseApiResource & {
  cloud_provider: 'gcp'
  resource_type: 'gcp.cloudfunctions.function'
  resource_data: GcpCloudFunctionApiResourceData
  risk_score_data?: BackendRiskScoreData
}

export type ComputeGcpRunServiceApiResource = ComputeBaseApiResource & {
  cloud_provider: 'gcp'
  resource_type: 'gcp.run.service'
  resource_data: GcpCloudRunServiceApiResourceData
  risk_score_data?: BackendRiskScoreData
}

// These type predicates are only necessary for narrowing because
// ComputeAwsEcsApiResource declares resource_type as a string rather than a
// concrete value, which eliminates the possibility of type narrowing
// discriminated unions.
export const isAwsLambdaResource = (
  r: ComputeResourceApiResponse,
): r is ComputeAwsLambdaApiResource => {
  return r.cloud_provider === 'aws' && r.resource_type === 'aws.lambda'
}

export const isAzureFunctionResource = (
  r: ComputeResourceApiResponse,
): r is ComputeAzureFunctionApiResource => {
  return r.cloud_provider === 'azure' && r.resource_type === 'azure.functions'
}

export const isAzureVMResource = (
  r: ComputeResourceApiResponse,
): r is ComputeAzureVMApiResource => {
  return (
    r.cloud_provider === 'azure' &&
    r.resource_type === 'azure.compute.virtualmachine'
  )
}

export const isAzureVMSSResource = (
  r: ComputeResourceApiResponse,
): r is ComputeAzureVMSSApiResource => {
  return (
    r.cloud_provider === 'azure' &&
    r.resource_type === 'azure.compute.virtualmachinescaleset'
  )
}

export const isAwsEc2Resource = (
  r: ComputeResourceApiResponse,
): r is ComputeAwsEc2ApiResource => {
  return r.cloud_provider === 'aws' && r.resource_type === 'aws.ec2'
}

export const isAwsEcsGenericResource = (
  r: ComputeResourceApiResponse,
): r is ComputeAwsEcsApiResource => {
  return (
    r.cloud_provider === 'aws' &&
    (r.resource_type === 'aws.ecs' || r.resource_type.startsWith('aws.ecs.'))
  )
}

export const isGcpInstanceResource = (
  r: ComputeResourceApiResponse,
): r is ComputeGcpInstanceApiResource => {
  return (
    r.cloud_provider === 'gcp' && r.resource_type === 'gcp.compute.instance'
  )
}

export const isGcpFunctionResource = (
  r: ComputeResourceApiResponse,
): r is ComputeGcpFunctionApiResource => {
  return (
    r.cloud_provider === 'gcp' &&
    r.resource_type === 'gcp.cloudfunctions.function'
  )
}

export const isGcpRunServiceResource = (
  r: ComputeResourceApiResponse,
): r is ComputeGcpRunServiceApiResource => {
  return r.cloud_provider === 'gcp' && r.resource_type === 'gcp.run.service'
}

// AWS Lambda-specific resource data
export type AwsLambdaApiResourceData = {
  function_name?: string
  state?: string
  logging_config?: {
    log_group?: string
    log_format?: string
  }
  runtime?: string
  package_type?: string
  architectures?: string[]
  memory_size?: number
  code_size?: number
  ephemeral_storage?: {
    size?: number
  }
  tags?: Record<string, string>

  // Other fields exist, but we don't need to access them directly
}

// Azure Functions-specific resource data
export type AzureFunctionApiResourceData = {
  // Identification
  function_app_name?: string
  function_name?: string
  resource_id?: string
  subscription_id?: string
  resource_group?: string
  region?: string

  // State
  state?: string

  // Hosting Configuration
  hosting_plan_type?: string // Consumption, Premium, AppService, Container
  sku_name?: string
  sku_tier?: string

  // Runtime Configuration
  runtime_version?: string
  runtime_stack?: string // dotnet, node, python, java, powershell
  os_type?: string // Windows, Linux

  // Scaling
  min_replicas?: number
  max_replicas?: number

  // Container Functions
  container_image?: string

  // Security & Networking
  always_on?: boolean
  https_only?: boolean
  http20_enabled?: boolean
  auth_enabled?: boolean
  allow_anonymous?: boolean
  vnet_integrated?: boolean
  managed_identity_type?: string
  app_insights_enabled?: boolean
  remote_debugging_enabled?: boolean
  cors_supports_credentials?: boolean
  private_endpoint_count?: number
  outbound_ip_addresses?: string[]

  // Metadata
  tags?: Record<string, string>
}

// ── Microsoft Defender for Endpoint — Host Context (FR-6.4.4) ──────────────

/**
 * The logon Defender observed on a host, from the alert's userEvidence bundle.
 *
 * There is deliberately no logon TYPE: neither userEvidence nor loggedOnUser
 * carries one, so account_domain ("AzureAD" for an Entra account, an NT domain
 * otherwise) is what distinguishes the kind of principal.
 */
export type HostContextLogon = {
  logon_identity: string
  account_domain: string
  is_machine_identity: boolean
  logon_timestamp: string | null
}

/**
 * One Windows host an AI workload runs on.
 *
 * `severity` carries the same caveat as everywhere else in the Defender
 * integration: it is empty until an alert names the device, and empty is NOT
 * 'none'.
 *
 * The two counts are separate because they open DIFFERENT dashboards.
 * `active_alert_count` is the PRE-TRIAGE concerns on the device's anchor, so it
 * agrees with what the Concerns deep-link opens; `confirmed_incident_count` is
 * the ones already promoted, which live on the Incidents page. Neither is the
 * raw Defender alert count the Compute drawer shows.
 */
export type HostContextDevice = {
  device_id: string
  name: string
  os_platform: string
  os_version: string
  agent_version: string
  severity: string
  health_status: string
  exposure_level: string
  last_seen_at: string | null
  correlation_state: string
  cloud_resource_id: string
  active_alert_count: number
  /** Concerns on the same anchor already confirmed, and so on the Incidents
   *  page rather than in the concerns queue. Optional: an API that predates the
   *  split omits it, and the card must not then render "undefined". */
  confirmed_incident_count?: number
  /** seccompose anchor this device's concerns compose onto; the filter value for
   *  BOTH the Concerns and Incidents deep-links. */
  anchor_value: string
  /** Full | Partial | Metadata | Blind -- the Shadow AI session-visibility grade. */
  host_session_context_grade: string
  logon?: HostContextLogon
}

// ── Microsoft Defender for Endpoint (FR-6.4.5) ─────────────────────────────

/**
 * Graph's `deviceRiskScore`, stored verbatim and lowercase.
 *
 * `unknownFutureValue` is part of the union deliberately: Graph emits it, and a
 * union that omitted it would turn a routine Microsoft addition into a type
 * error at the next backend deploy.
 */
export type DefenderSeverity =
  'none' | 'informational' | 'low' | 'medium' | 'high' | 'unknownFutureValue'

/**
 * The Defender facts for a compute resource's correlated device. Field names
 * are the API's own snake_case.
 *
 * Nearly everything is nullable. Notably `severity` and `risk_score` are null
 * until an alert names the device -- they arrive on the ALERT path, from
 * deviceEvidence.riskScore, not from the device sync. Null is therefore NOT the
 * same as 'none' (which Graph defines as "there are no alerts related to this
 * device"), and neither may be rendered as a low risk band.
 *
 * There is deliberately no numeric companion to `severity`. FR-6.4.3 defines
 * Defender's "risk score" AS this categorical level, and the AI-workload risk
 * factor keys on the string, so a derived number restated it while sitting
 * beside the resource engine's own computed score on a different scale.
 *
 * `health_status`, `exposure_level` and `correlation_state` are plain strings
 * rather than unions for the same reason DefenderSeverity carries
 * `unknownFutureValue`: Graph adds values, and a too-narrow union converts new
 * data into a build failure.
 */
export type DefenderDeviceApiData = {
  device_id: string
  computer_dns_name: string | null
  severity: DefenderSeverity | null
  health_status: string | null
  exposure_level: string | null
  os_platform: string | null
  os_version: string | null
  agent_version: string | null
  last_seen_at: string | null
  correlation_state: string | null
  /**
   * Raw Defender alerts naming this device. NOT comparable to the concern and
   * security-issue counts shown elsewhere on the same drawer.
   */
  defender_alert_count: number | null
}

/**
 * `defender` is null when the resource exists but no Defender device correlates
 * to it -- the common case, and a normal 200 rather than an error.
 */
export type ComputeDefenderApiResponse = {
  resource_id: string
  defender: DefenderDeviceApiData | null
}

// Azure Virtual Machine-specific resource data
export type AzureVMApiResourceData = {
  id: string
  name: string
  location: string
  type: string
  tags?: Record<string, string>
  identity?: {
    type: string
    principalId?: string
    tenantId?: string
  }
  properties: {
    vmId: string
    provisioningState: string
    hardwareProfile: {
      vmSize: string // "Standard_B2s", "Standard_D4s_v3", etc.
    }
    storageProfile: {
      imageReference?: {
        publisher: string
        offer: string
        sku: string
        version: string
      }
      osDisk: {
        name: string
        osType: 'Linux' | 'Windows'
        diskSizeGB: number
        caching: string
        managedDisk?: {
          storageAccountType: string // "Premium_LRS", "Standard_LRS", etc.
          id: string
        }
      }
      dataDisks: Array<{
        name: string
        diskSizeGB: number
        lun: number
      }>
    }
    networkProfile: {
      networkInterfaces: Array<{
        id: string
        properties?: {
          primary: boolean
        }
      }>
    }
  }
}

// Azure Virtual Machine Scale Set-specific resource data
export type AzureVMSSApiResourceData = {
  id: string
  name: string
  location: string
  type: string
  tags?: Record<string, string>
  zones?: string[]
  identity?: {
    type: string
    principalId?: string
    tenantId?: string
    userAssignedIdentities?: Record<string, unknown>
  }
  sku?: {
    name: string // VM size e.g. "Standard_D2s_v3"
    tier?: string
    capacity?: number // number of VM instances
  }
  properties: {
    provisioningState: string
    orchestrationMode?: string
    upgradePolicy?: {
      mode: string
    }
    virtualMachineProfile?: {
      osProfile?: {
        computerNamePrefix?: string
        adminUsername?: string
        linuxConfiguration?: {
          disablePasswordAuthentication?: boolean
        }
      }
      storageProfile?: {
        osDisk?: {
          osType?: string
          name?: string
          caching?: string
          createOption?: string
          diskSizeGB?: number
          managedDisk?: {
            storageAccountType?: string
          }
        }
        imageReference?: {
          publisher?: string
          offer?: string
          sku?: string
          version?: string
        }
      }
      networkProfile?: {
        networkInterfaceConfigurations?: Array<{
          name: string
          properties?: {
            primary?: boolean
            enableAcceleratedNetworking?: boolean
            networkSecurityGroup?: {
              id: string
            }
            ipConfigurations?: Array<{
              name: string
              properties?: {
                subnet?: {
                  id: string
                }
              }
            }>
          }
        }>
      }
      extensionProfile?: {
        extensions?: Array<{
          name: string
          properties?: {
            publisher?: string
            type?: string
            typeHandlerVersion?: string
            autoUpgradeMinorVersion?: boolean
          }
        }>
      }
    }
  }
}

// Azure Container Images-specific resource data
export type AzureContainerImageApiResourceData = {
  // Operating System
  os?: string

  // Container Image Properties
  tags?: string[]
  digest?: string
  architecture?: string
  imageSizeBytes?: number

  // Registry Information
  registryName?: string
  repositoryName?: string
  registryResourceId?: string

  // Technical Metadata (optional, less important for display)
  configMediaType?: string
  manifestMediaType?: string
  scanResults?: unknown
}

// Backend API response - unwrapped instance data (no "instance" wrapper)
export type AwsEc2ApiResourceData = {
  // AWS returns nil/null when an EC2 instance has no tags; mark as nullable to
  // force callers to null-check before iterating.
  Tags: { Key: string; Value: string }[] | null
  // These nested objects come straight from DescribeInstances and are present on
  // healthy payloads, but a partial/seed payload (or the SQL fallback that emits
  // just {"Tags": []} for a malformed row) can omit them. Mark optional so callers
  // are forced to null-check rather than crashing on `Cannot read properties of
  // undefined` (the PENG-12883 bug class).
  State?: { Name: string }
  Monitoring?: { State: string }
  InstanceType: string
  Architecture: string
  CpuOptions?: {
    CoreCount: number
    ThreadsPerCore: number
  }

  // Other fields exist, but we don't need to access them directly
}

// Generic ECS resource data - accommodates various ECS resource types with common fields
export type AwsEcsGenericApiResourceData = {
  // Common ECS identifiers
  arn?: string
  cluster_arn?: string
  service_arn?: string
  task_arn?: string
  container_instance_arn?: string

  // Common status and naming
  status?: string
  name?: string

  // Container instance specific (aws.ecs.container-instance)
  ec2_instance_id?: string
  capacity_provider_name?: string
  running_tasks_count?: number
  pending_tasks_count?: number
  active_services_count?: number
  agent_connected?: boolean

  // Service specific (aws.ecs.service)
  desired_count?: number
  running_count?: number
  pending_count?: number
  task_definition?: string
  launch_type?: string
  platform_version?: string

  // Cluster specific (aws.ecs.cluster)
  cluster_name?: string

  // Task specific (aws.ecs.task)
  task_definition_arn?: string
  last_status?: string
  desired_status?: string
  cpu?: string
  memory?: string
  connectivity?: string
  connectivity_at?: string

  // Common resource fields
  attributes?: Array<{
    name: string
    value?: string
    target_type?: string
    target_id?: string
  }>
  registered_resources?: Array<{
    name: string
    type: string
    double_value?: number
    long_value?: number
    integer_value?: number
    string_set_value?: string[]
  }>
  remaining_resources?: Array<{
    name: string
    type: string
    double_value?: number
    long_value?: number
    integer_value?: number
    string_set_value?: string[]
  }>
  version_info?: {
    agent_version?: string
    agent_hash?: string
    docker_version?: string
  }
  tags?: Array<{
    key: string
    value: string
  }>

  // Catch-all for any other fields that may exist
  [key: string]: unknown
}

// GCP Compute Engine instance (Compute API v1 Instance). Field names follow the
// API's JSON representation; only what the UI reads is typed, the rest is kept
// as unknown so the drawer can still show it raw.
export type GcpComputeInstanceApiResourceData = {
  name?: string
  status?: string // PROVISIONING | STAGING | RUNNING | STOPPING | SUSPENDED | TERMINATED | ...
  machineType?: string // projects/{project}/zones/{zone}/machineTypes/{type}
  zone?: string
  creationTimestamp?: string
  labels?: Record<string, string>
  disks?: Array<{
    diskSizeGb?: string // int64 serialised as a string
    type?: string // PERSISTENT | SCRATCH
    boot?: boolean
  }>
  networkInterfaces?: Array<{
    network?: string
    subnetwork?: string
    networkIP?: string
  }>
  serviceAccounts?: Array<{ email?: string; scopes?: string[] }>
  [key: string]: unknown
}

// GCP Cloud Functions (Cloud Functions API v2 Function)
export type GcpCloudFunctionApiResourceData = {
  name?: string // projects/{project}/locations/{location}/functions/{function}
  state?: string // ACTIVE | FAILED | DEPLOYING | DELETING | UNKNOWN | ...
  environment?: string // GEN_1 | GEN_2
  labels?: Record<string, string>
  updateTime?: string
  buildConfig?: { runtime?: string; entryPoint?: string }
  serviceConfig?: {
    availableMemory?: string
    maxInstanceCount?: number
    minInstanceCount?: number
    timeoutSeconds?: number
    serviceAccountEmail?: string
    ingressSettings?: string
  }
  [key: string]: unknown
}

// GCP Cloud Run service (Cloud Run Admin API v1 Service, Knative shape)
export type GcpCloudRunServiceApiResourceData = {
  apiVersion?: string
  kind?: string
  metadata?: {
    name?: string
    namespace?: string
    labels?: Record<string, string>
    annotations?: Record<string, string>
    creationTimestamp?: string
  }
  spec?: {
    template?: {
      spec?: {
        containers?: Array<{
          image?: string
          resources?: { limits?: Record<string, string> }
        }>
        serviceAccountName?: string
        containerConcurrency?: number
        timeoutSeconds?: number
      }
    }
  }
  status?: {
    url?: string
    latestReadyRevisionName?: string
    conditions?: Array<{ type?: string; status?: string }>
  }
  [key: string]: unknown
}

// Multi-cloud compute resource API response
export type ComputeResourceApiResponse =
  | ComputeAwsLambdaApiResource
  | ComputeAwsEc2ApiResource
  | ComputeAwsEcsApiResource
  | ComputeAzureFunctionApiResource
  | ComputeAzureVMApiResource
  | ComputeAzureVMSSApiResource
  | ComputeGcpInstanceApiResource
  | ComputeGcpFunctionApiResource
  | ComputeGcpRunServiceApiResource

// Compute Resources Filter Parameters (snake_case to match backend)
// Shared between list and stats endpoints
export type ComputeResourcesFilterParams = {
  providers?: string[]
  categories?: string[]
  regions?: string[]
  accounts?: string[]
  statuses?: string[]
  environments?: string[]
  source_system?: string[]
  risk_categories?: string[]
  bi_levels?: string[] // snake_case
  ids?: string[]
  resource_id?: string[] // snake_case - cloud resource identifier (ARN, etc.)
  search_query?: string // snake_case
  issues?: string[] // Security issues array parameter
  has_vuln?: boolean // snake_case (legacy)
  has_incident?: boolean // snake_case (legacy)
  has_event?: boolean // snake_case (legacy)
  has_misconfig?: boolean // snake_case (legacy)
  risk_indicators?: string[] // snake_case
  date_from?: string
  date_to?: string
  timestamp_type?: string
}

// Compute Resources API Request (includes filters + sorting + pagination)
export type ComputeResourcesApiRequest = ComputeResourcesFilterParams & {
  sort_by?: string // snake_case
  sort_order?: string // snake_case
  limit?: number | null
  offset?: number | null
}

// Network Resources Filter Parameters (snake_case to match backend)
// Shared between list and stats endpoints
export type NetworkResourcesFilterParams = {
  providers?: string[]
  categories?: string[]
  regions?: string[]
  accounts?: string[]
  statuses?: string[]
  environments?: string[]
  source_system?: string[]
  risk_categories?: string[]
  ids?: string[]
  resource_id?: string[] // snake_case - cloud resource identifier (ARN, etc.)
  search_query?: string // snake_case
  issues?: string[] // Security issues array parameter
  has_vuln?: boolean // snake_case (legacy)
  has_incident?: boolean // snake_case (legacy)
  has_event?: boolean // snake_case (legacy)
  has_misconfig?: boolean // snake_case (legacy)
  risk_indicators?: string[] // snake_case
  date_from?: string
  date_to?: string
  timestamp_type?: string
}

// Network Resources API Request (includes filters + sorting + pagination)
export type NetworkResourcesApiRequest = NetworkResourcesFilterParams & {
  sort_by?: string // snake_case
  sort_order?: string // snake_case
  limit: number
  offset: number
}

// Compute Resources API Response (uses standard pagination)
export type ComputeResourcesApiResponseWrapper =
  PaginatedResponse<ComputeResourceApiResponse>

// Standard pagination metadata (matches backend models.PaginationInfo)
// Used across all inventory, risk management, and rules list endpoints.
// is_estimated is optional because only some backends (e.g. very large tables
// where COUNT(*) is approximated) populate it; the field rides as false otherwise.
export type PaginationMetadata = {
  total: number
  page: number
  limit: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
  is_estimated?: boolean
}

// Standard paginated response wrapper
export type PaginatedResponse<T> = {
  items: T[]
  pagination: PaginationMetadata
}

// S3 Storage API Types
export type S3BucketApiResponse = {
  id: number
  bucket_arn: string
  bucket_name: string
  tenant_id: number
  account_id: string
  region_name: string
  creation_date: string
  tags: Record<string, string>
  replication_enabled: boolean
  replication_config?: Record<string, unknown>
  is_deleted: boolean
  deleted_at?: string
  created_at: string
  updated_at: string
  security_issues: SecurityIssueToCount
} & ApiBusinessImpactValues

// API Error response
export type ApiError = {
  error: string
  message: string
  code: number
}

// checkResponseOk accepts a Response from the fetch API and checks that its
// status is OK, meaning the HTTP status code is 200-299. If the status is OK,
// the response is returned as-is, without modification.
//
// If the status is not OK, it attempts to get an ApiError type from the
// response and use the contained message for the Error that this function
// throws. If there is no error message, or if the failed response was not valid
// JSON, a fallback message that includes the HTTP status code and text is
// thrown as the Error.
export const checkApiResponseOk = async (
  response: Response,
): Promise<Response> => {
  if (!response.ok) {
    const fallback = `HTTP: ${response.status}: ${response.statusText}`
    let error: ApiError | undefined
    try {
      error = await response.json()
    } catch {
      void 0
    }
    throw new Error(error?.message || fallback)
  }
  return response
}
